import { Box, Divider, Flex } from '@chakra-ui/react';
import { useTheme } from '@emotion/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import {
  updateConfigData,
  updateMetaData,
  useConfigDataSelector,
  useTemplateMetaDataSelector,
} from 'screens/admin/Objectives/GoalPreferences/slice/accessControlSlice';

import { Objective } from 'assets/assetsV1/iconComponents/Objective';

import { modifyString, StringModifier } from 'constants/copyHelpers';

import { TextBodyLarge, TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { VisibilitySwitch } from '../../styles';

const VisibilitySettings = ({ isEditMode }) => {
  const { configData } = useConfigDataSelector();
  const { includeCollaboratorGoals } = useTemplateMetaDataSelector();
  const { moduleName } = useOrganisationSettings();
  const theme = useTheme();
  const dispatch = useDispatch();
  const onChange = (key, value) => {
    dispatch(updateConfigData({ key, value }));
  };

  const onChangeOwnersAndCollaborators = (key, value) => {
    dispatch(updateMetaData({ key, value }));
  };

  return (
    <Box padding="1rem">
      <Flex mb="1rem" justifyContent="space-between" alignItems="center">
        <Objective boxSize="24px" color="#666666" />
        <Box flexGrow={1} ml="8px">
          <TextBodyLarge
            sx={{
              fontWeight: '600',
            }}
          >
            Visibility settings
          </TextBodyLarge>
          <TextLabel color="text.g2">
            Choose the default visibility when creating Objectives and Key Result Owners, Collaborators and their
            Managers
          </TextLabel>
        </Box>
      </Flex>
      <Divider />

      <Flex justifyContent="space-between" alignItems="center" p="1rem">
        <TextBodyMedium>Default objective visibility</TextBodyMedium>

        <Flex bgColor={theme.colors.neutral.EBEFF3} p={2} borderRadius={4}>
          <VisibilitySwitch
            onClick={() => {
              onChange('DEFAULT_OBJECTIVE_VISIBILITY', 'GLOBAL');
            }}
            disabled={!isEditMode}
            variant={configData?.DEFAULT_OBJECTIVE_VISIBILITY?.value === 'GLOBAL' ? 'contained' : 'tertiary'}
          >
            Public
          </VisibilitySwitch>
          <VisibilitySwitch
            onClick={() => {
              onChange('DEFAULT_OBJECTIVE_VISIBILITY', 'PRIVATE');
            }}
            disabled={!isEditMode}
            variant={configData?.DEFAULT_OBJECTIVE_VISIBILITY?.value === 'PRIVATE' ? 'contained' : 'tertiary'}
          >
            Private
          </VisibilitySwitch>
        </Flex>
      </Flex>
      <Divider />

      <Flex justifyContent="space-between" alignItems="center" p="1rem">
        <TextBodyMedium>Default key result visibility</TextBodyMedium>

        <Flex bgColor={theme.colors.neutral.EBEFF3} p={2} borderRadius={4}>
          <VisibilitySwitch
            onClick={() => {
              onChange('DEFAULT_KR_VISIBILITY', 'GLOBAL');
            }}
            disabled={!isEditMode}
            variant={configData?.DEFAULT_KR_VISIBILITY?.value === 'GLOBAL' ? 'contained' : 'tertiary'}
          >
            Public
          </VisibilitySwitch>
          <VisibilitySwitch
            onClick={() => {
              onChange('DEFAULT_KR_VISIBILITY', 'PRIVATE');
            }}
            disabled={!isEditMode}
            variant={configData?.DEFAULT_KR_VISIBILITY?.value === 'PRIVATE' ? 'contained' : 'tertiary'}
          >
            Private
          </VisibilitySwitch>
        </Flex>
      </Flex>
      <Divider />

      <Flex justifyContent="space-between" alignItems="center" p="1rem">
        <Box width="75%">
          <TextBodyMedium>{modifyString(moduleName, StringModifier.TITLE_CASE)} Ownership Display</TextBodyMedium>
          <TextLabel color="text.g2">
            Defines {modifyString(moduleName, StringModifier.SENTENCE_CASE)} visibility across the platform based on
            user role. Affects display on {modifyString(moduleName, StringModifier.SENTENCE_CASE)} pages, profiles, and
            overviews.
          </TextLabel>
        </Box>
        <Flex bgColor={theme.colors.neutral.EBEFF3} p={2} borderRadius={4}>
          <VisibilitySwitch
            onClick={() => {
              // CHANGE KEY AND VALUE
              onChangeOwnersAndCollaborators('includeCollaboratorGoals', false);
            }}
            disabled={!isEditMode}
            variant={!includeCollaboratorGoals ? 'contained' : 'tertiary'}
          >
            Owners only
          </VisibilitySwitch>
          <VisibilitySwitch
            onClick={() => {
              onChangeOwnersAndCollaborators('includeCollaboratorGoals', true);
            }}
            disabled={!isEditMode}
            variant={includeCollaboratorGoals ? 'contained' : 'tertiary'}
          >
            Owners & collaborators
          </VisibilitySwitch>
        </Flex>
      </Flex>
      <Divider />
    </Box>
  );
};

export default VisibilitySettings;
